import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CountryFlag from './CountryFlag';
import { useMasterLabel } from '../../hooks/useMasterLabel';

// Per-row translated country name.
const CountryName = ({ country }) => {
    const label = useMasterLabel('masterCountry', country?.countryId, 'countryName', country?.countryName || '');
    return <>{label}</>;
};

/**
 * Inline phone-country picker. Renders as a small button (flag + dial code) that
 * lives inside the phone input border. Clicking it opens a searchable dropdown
 * of countries. Selecting a country fires `onSelect({ countryId, timeZone, countryName })`
 * so callers can keep the form's single source of truth (formData.countryId) in
 * sync with both the phone dial code and the address country.
 */
const PhoneCountryDropdown = ({
    countryTimezoneList = [],
    countryId,
    onSelect,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    const selected = countryTimezoneList.find((c) => c.countryId === countryId) || null;

    const filtered = countryTimezoneList.filter((c) => {
        const q = search.toLowerCase();
        return (
            c.countryName?.toLowerCase().includes(q) ||
            c.countryCode?.toLowerCase().includes(q)
        );
    });

    useEffect(() => {
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', onClickOutside);
            return () => document.removeEventListener('mousedown', onClickOutside);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const handlePick = (item) => {
        onSelect({
            countryId: item.countryId,
            timeZone: item.timeZone,
            countryName: item.countryName,
        });
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div ref={containerRef} className="relative flex-shrink-0" data-testid="phone-country-dropdown">
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setIsOpen((v) => !v)}
                className={`flex items-center gap-1 pr-1 mr-1 text-sm font-medium text-gray-700 ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:text-[#4ABDBD] cursor-pointer'}`}
                data-testid="phone-country-dropdown-trigger"
            >
                {selected ? (
                    <>
                        <CountryFlag countryName={selected.countryName} alt={selected.countryName} className="w-5 h-3.5 object-cover rounded-sm" />
                        <span>{selected.countryCode}</span>
                    </>
                ) : (
                    <span className="text-gray-400">{t('common.select', 'Select')}</span>
                )}
                <ChevronDown data-testid="phone-country-dropdown-chevron-icon" className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && !disabled && (
                <div
                    className="absolute z-50 left-0 top-full mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                    data-testid="phone-country-dropdown-menu"
                >
                    <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                            <Search data-testid="phone-country-dropdown-search-icon" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('common.search_country', 'Search country...')}
                                className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] placeholder-gray-400"
                                data-testid="phone-country-dropdown-search"
                            />
                        </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto custom-scrollbar" data-testid="phone-country-dropdown-options-list">
                        {filtered.length === 0 ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-400" data-testid="phone-country-dropdown-no-results-text">
                                {t('common.no_countries_found', 'No countries found')}
                            </div>
                        ) : (
                            filtered.map((c) => {
                                const isActive = countryId === c.countryId;
                                return (
                                    <button
                                        key={`${c.countryId}-${c.timeZone}`}
                                        type="button"
                                        onClick={() => handlePick(c)}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isActive ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                                        data-testid={`phone-country-dropdown-option-${c.countryId}`}
                                    >
                                        <CountryFlag countryName={c.countryName} alt={c.countryName} className="w-5 h-3.5 object-cover rounded-sm shrink-0" />
                                        <span className="truncate flex-1 text-left"><CountryName country={c} /></span>
                                        <span className="text-gray-500 text-xs">{c.countryCode}</span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhoneCountryDropdown;
